import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faUser, faBriefcase, faIdCard, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS } from '../config/apiConfig';

function EditMember() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    profession: '',
    organization: '',
    designation: '',
    experience: '',
    membershipType: '',
    status: '',
    joinDate: '',
    expiryDate: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchMember();
  }, [id]);
  
  const fetchMember = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(ADMIN_URLS.MEMBER_DETAIL(id), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch member details');
      }
      
      const data = await response.json();
      setFormData({
        ...data,
        joinDate: data.join_date ? data.join_date.slice(0, 10) : '',
        expiryDate: data.expiry_date ? data.expiry_date.slice(0, 10) : ''
      });
      
    } catch (error) {
      console.error('Error fetching member details:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.membershipType) newErrors.membershipType = 'Please select a membership type';
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(ADMIN_URLS.MEMBER_DETAIL(id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update member');
      }
      
      // Redirect to member details page on success
      navigate(`/admin/members/${id}`);
      
    } catch (error) {
      console.error('Error updating member:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="admin-loading-spinner"></div>
          <p>Loading member information...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-error">
          <div className="admin-error-icon">⚠️</div>
          <h3>Error: {error}</h3>
          <button 
            className="admin-btn admin-btn-primary" 
            onClick={() => navigate('/admin/members')}
          >
            Back to Members
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Edit Member</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/members">Members</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to={`/admin/members/${id}`}>{formData.firstName} {formData.lastName}</Link>
          </li>
          <li className="admin-breadcrumb-item active">Edit</li>
        </ul>
      </div>
      
      <div className="admin-cards-container">
        <form onSubmit={handleSubmit} className="admin-form admin-form-enhanced">
          <div className="admin-card admin-card-elevated">
            <div className="admin-card-header admin-card-header-accent">
              <FontAwesomeIcon icon={faUser} className="admin-card-header-icon" />
              <h2 className="admin-card-title">Personal Information</h2>
            </div>
            <div className="admin-card-body">
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="firstName" className="admin-form-label">First Name <span className="admin-required">*</span></label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.firstName ? 'admin-input-error' : ''}`}
                  />
                  {errors.firstName && <div className="admin-error-message">{errors.firstName}</div>}
                </div>
                
                <div className="admin-form-group">
                  <label htmlFor="lastName" className="admin-form-label">Last Name <span className="admin-required">*</span></label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.lastName ? 'admin-input-error' : ''}`}
                  />
                  {errors.lastName && <div className="admin-error-message">{errors.lastName}</div>}
                </div>
              </div>
              
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="email" className="admin-form-label">Email <span className="admin-required">*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.email ? 'admin-input-error' : ''}`}
                  />
                  {errors.email && <div className="admin-error-message">{errors.email}</div>}
                </div>
                
                <div className="admin-form-group">
                  <label htmlFor="phone" className="admin-form-label">Phone <span className="admin-required">*</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.phone ? 'admin-input-error' : ''}`}
                  />
                  {errors.phone && <div className="admin-error-message">{errors.phone}</div>}
                </div>
              </div>
            </div>
          </div>
          
          <div className="admin-card admin-card-elevated">
            <div className="admin-card-header admin-card-header-accent">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="admin-card-header-icon" />
              <h2 className="admin-card-title">Address Information</h2>
            </div>
            <div className="admin-card-body">
              <div className="admin-form-row">
                <div className="admin-form-group" style={{ flex: '1 1 100%' }}>
                  <label htmlFor="address" className="admin-form-label">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                    placeholder="Street address"
                  />
                </div>
              </div>
              
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="city" className="admin-form-label">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                    placeholder="City"
                  />
                </div>
                
                <div className="admin-form-group">
                  <label htmlFor="state" className="admin-form-label">State</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                    placeholder="State"
                  />
                </div>
                
                <div className="admin-form-group">
                  <label htmlFor="pincode" className="admin-form-label">Pincode</label>
                  <input
                    type="text"
                    id="pincode"
                    name="pincode"
                    value={formData.pincode || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                    placeholder="Pincode"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="admin-card admin-card-elevated">
            <div className="admin-card-header admin-card-header-accent">
              <FontAwesomeIcon icon={faBriefcase} className="admin-card-header-icon" />
              <h2 className="admin-card-title">Professional Information</h2>
            </div>
            <div className="admin-card-body">
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="profession" className="admin-form-label">Profession</label>
                  <input
                    type="text"
                    id="profession"
                    name="profession"
                    value={formData.profession || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                  />
                </div>
                
                <div className="admin-form-group">
                  <label htmlFor="organization" className="admin-form-label">Organization</label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                  />
                </div>
              </div>
              
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="designation" className="admin-form-label">Designation</label>
                  <input
                    type="text"
                    id="designation"
                    name="designation"
                    value={formData.designation || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                  />
                </div>
                
                <div className="admin-form-group">
                  <label htmlFor="experience" className="admin-form-label">Experience</label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience || ''}
                    onChange={handleChange}
                    className="admin-form-control admin-form-select"
                  >
                    <option value="">Select Years of Experience</option>
                    <option value="0-2">0-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="10+">More than 10 years</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <div className="admin-card admin-card-elevated">
            <div className="admin-card-header admin-card-header-accent">
              <FontAwesomeIcon icon={faIdCard} className="admin-card-header-icon" />
              <h2 className="admin-card-title">Membership Information</h2>
            </div>
            <div className="admin-card-body">
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="membershipType" className="admin-form-label">Membership Type <span className="admin-required">*</span></label>
                  <select
                    id="membershipType"
                    name="membershipType"
                    value={formData.membershipType}
                    onChange={handleChange}
                    className={`admin-form-control admin-form-select ${errors.membershipType ? 'admin-input-error' : ''}`}
                  >
                    <option value="">Select Membership Type</option>
                    <option value="primary_member">Primary Member</option>
                    <option value="associate_member">Associate Member</option>
                    <option value="student_membership">Student Member</option>
                    <option value="financial_collegium">Financial Collegium</option>
                    <option value="academic_collegium">Academic Collegium</option>
                    <option value="institutional_collegium">Institutional Collegium</option>
                  </select>
                  {errors.membershipType && <div className="admin-error-message">{errors.membershipType}</div>}
                </div>
                
                <div className="admin-form-group">
                  <label htmlFor="status" className="admin-form-label">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="admin-form-control admin-form-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Join Date</label>
                  <input
                    type="date"
                    name="joinDate"
                    value={formData.joinDate || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Membership Validity</label>
                  <input
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate || ''}
                    onChange={handleChange}
                    className="admin-form-control"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="admin-form-actions">
            <Link
              to={`/admin/members/${id}`}
              className="admin-btn admin-btn-light admin-btn-with-icon"
            >
              <FontAwesomeIcon icon={faTimes} />
              <span>Cancel</span>
            </Link>
            <button
              type="submit"
              className="admin-btn admin-btn-primary admin-btn-with-icon"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faSave} />
              <span>{isSubmitting ? 'Saving...' : 'Update Member'}</span>
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

export default EditMember;
